import React, { useCallback, useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { MAGAZINE_LOG_EVENT } from '@/constants/logs/magazine';
import useAppDownloadCujModal from '@/hooks/cuj/useAppDownloadCujModal';
import useGetMagazineDetail from '@/hooks/magazine/useGetMagazineDetail';
import useGetPrevNextMagazinePid from '@/hooks/magazine/useGetPrevNextMagazinePid';
import useGetRelatedMagazine from '@/hooks/magazine/useGetRelatedMagazine';
import { useCompleteMissionMutation } from '@/hooks/mission/mission.gql.impl';
import { UserMissionTypeVo } from '@/hooks/mission/types';
import { useBroadcastChannel } from '@/hooks/mission/useBroadcastChannel';
import useMissionTimer from '@/hooks/mission/useMissionTimer';
import useCanProgressQnaWrite from '@/hooks/qna/mobile/write/useCanProgressQnaWrite';
import useGetMagazineRelatedQna from '@/hooks/qna/useGetMagazineRelatedQna';
import useAuthModal from '@/hooks/useAuthModal';
import useClickContentLike from '@/hooks/useClickContentLike';
import useCustomRouter from '@/hooks/useCustomRouter';
import useIsResponsive from '@/hooks/useIsResponsive';
import useModal from '@/hooks/useModal';
import useMoveToHospitalSearch from '@/hooks/useMoveToHospitalSearch';
import useMoveToTotalSearch from '@/hooks/useMoveToTotalSearch';
import useToast from '@/hooks/useToast';
import { tokenState } from '@/recoil/atoms/common/auth';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import {
  InternalMagazineTag,
  MagazineBottomButtonType,
  MagazineDetailVo,
  MagazineTagType,
  MagazineType,
  RelatedMagazinesVo,
} from '@/types/magazine';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { RelatedQnaVo } from '@/types/qna';
import { logger, magazineLogger, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

import { EPixelEventName } from '@/constants/pixel';
import useAdBanner from '@/hooks/advertisement/useAdBanner';
import { AdPageType, BannerAdExchangeModel } from '@/models/ad/list';
import Pixel from '@/utils/pixel/pixel';

import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { DynamicLinkOptions, EAirbridgeLinkType } from '@/shared/types';
import useNavigate from '../useNavigate';

interface Props extends CustomPageProps {
  magazinePid: string;
  missionId?: string;
}

export interface MagazineDetailViewModel {
  bannerAdExchange: BannerAdExchangeModel | null;
  totalRelatedMagazines: RelatedMagazinesVo[];
  magazine?: MagazineDetailVo;
  departmentTags: InternalMagazineTag[];
  symptomTags: InternalMagazineTag[];
  diseaseTags: InternalMagazineTag[];
  totalRelatedMagazinesTitle: string;
  magazineContent: string;
  magazineTagNames: string[];
  prevMagazinePid: string | null;
  nextMagazinePid: string | null;
  pageName: string;
  magazineRelatedQna?: RelatedQnaVo[];
  copyUrl: string;
  bottomButtonText: string;
  timerVisible: boolean;
  displayTime: string;
  missionTargetTime: number;
  missionCompleted: boolean;
  handleSendExposureLog: () => void;
  handleLikeClick: () => void;
  onClickShare: () => void;
  onRecordLog: (logName: string) => void;
  onClickTag: (tagName: string) => void;
  moveToSearch: () => void;
  moveToHospital: (coreId: string) => void;
  moveToTotalSearch: (keyword: string) => void;
  handleStickyButton: () => void;
  moveOtherMagazine: (magazine: RelatedMagazinesVo) => void;
  moveToAuthorHospital: (isDoctorProfile: boolean) => void;
  magazineTocLogger: () => void;
  onClickPrevNextMagazine: (magazinePid: string) => void;
  onClickRelatedQna: (questionPid: string) => void;
  onClickOuterLink: (url: string) => void;
  extractTextFromChildren: (children: React.ReactNode) => string;
  onClickHospitalCta: (hospitalPid: string) => void;
  goBack: () => void;
  onClickMagazineBannerOutLink: (outLink: string) => void;
  shouldShowEventText: () => boolean;
  navigateToMagazineAuthorDetail: () => void;
  onClickTopCta: () => void;
  onClickOuterLinkWithLinkText: (url: string) => void;
  airbridgeLinkOptions: DynamicLinkOptions;
  getAirbridgeDynamicLink: (options: DynamicLinkOptions, linkType?: EAirbridgeLinkType) => string;
  isEnabledWebDiagnosisCategory: boolean;
}

const useMagazineDetailViewModel = ({ magazinePid, client, missionId }: Props): MagazineDetailViewModel => {
  const { pageName, moveToPage } = useCustomRouter();
  const { showToast } = useToast();
  const { setShowAuthModal } = useAuthModal(true);
  const token = useRecoilValue(tokenState);
  const { showModal, closeModal } = useModal();
  const { relatedMagazine: magazines } = useGetRelatedMagazine(magazinePid);
  const { prevMagazinePid, nextMagazinePid } = useGetPrevNextMagazinePid(magazinePid, MagazineType.ARTICLE);
  const { moveToHospital } = useMoveToHospitalSearch();
  const { moveToTotalSearch } = useMoveToTotalSearch();
  const { isMobile } = useIsResponsive();
  const { likeContent, cancelLikeContent } = useClickContentLike(magazinePid);
  const magazine = useGetMagazineDetail(magazinePid);
  const { magazineRelatedQna } = useGetMagazineRelatedQna(magazinePid);
  const completeMissionMutation = useCompleteMissionMutation();
  const broadCastChannel = useBroadcastChannel<UserMissionTypeVo>('user-missions');
  const navigate = useNavigate();
  const airbridgeDynamicLink = useAirbridgeDynamicLink();

  const { bannerAdExchange, handleSendExposureLog } = useAdBanner(AdPageType.MagazineDetail, client);

  const MISSION_TARGET_TIME = 10;
  const { displayTime, missionCompleted } = useMissionTimer({
    activated: Boolean(missionId),
    targetTime: MISSION_TARGET_TIME,
  });

  const copyUrl = useMemo(
    () => `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.MAGAZINE.DETAIL.path}/${magazine?.magazinePid}`,
    [magazine?.magazinePid]
  );

  const magazineContent = useMemo(() => {
    if (isWebView()) {
      return magazine?.mobileContent;
    }

    return magazine?.content;
  }, [magazine?.content, magazine?.mobileContent]);

  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  const { clickProgressWrite } = useCanProgressQnaWrite();

  const departmentTags = useMemo(
    () => magazine?.tags.filter((tag) => tag.tagType === MagazineTagType.DEPARTMENT),
    [magazine?.tags]
  );
  const diseaseTags = useMemo(
    () => magazine?.tags.filter((tag) => tag.tagType === MagazineTagType.DISEASE),
    [magazine?.tags]
  );
  const symptomTags = useMemo(
    () => magazine?.tags.filter((tag) => tag.tagType === MagazineTagType.SYMPTOM),
    [magazine?.tags]
  );
  const totalRelatedMagazinesTitle = useMemo(
    () => (isMobile ? '다른 콘텐츠도 읽어보세요' : '이런 콘텐츠는 어떠세요?'),
    [isMobile]
  );
  const magazineTagNames: string[] = useMemo(() => {
    return magazine && magazine.tagNames
      ? magazine.tagNames.map((name) => {
          return name;
        })
      : [];
  }, [magazine]);

  const bottomButtonText: string = useMemo(() => {
    const shouldShowContraceptiveText =
      magazine?.tagNames?.some((tag) => tag.includes('사후피임')) && !client.isWebView;
    if (shouldShowContraceptiveText) {
      return '빠르게 사후피임약 처방받기';
    }

    switch (magazine?.bottomButtonType) {
      case MagazineBottomButtonType.DIAGNOSIS:
        return '비대면 진료 받으러 가기';
      case MagazineBottomButtonType.QUESTION:
      default:
        return '지금 바로 의료 상담받기';
    }
  }, [magazine?.bottomButtonType, client.isWebView]);

  const shouldShowEventText = () => !isWebView();

  const airbridgeLinkOptions: DynamicLinkOptions = {
    sub_id: magazine?.magazinePid,
    sub_id_1: magazine?.title,
    sub_id_2: diseaseTags?.[0]?.name || '',
    sub_id_3: departmentTags?.[0]?.name || '',
  };

  const getAirbridgeDynamicLink = (
    options: DynamicLinkOptions,
    linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL
  ) => {
    return airbridgeDynamicLink.getDynamicLink(options, linkType);
  };

  const bottomButtonDynamicLink = () => {
    airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
      campaign: 'web_content_magazine',
      content: 'web_content_flt_details',
      term: bottomButtonText,
      ...airbridgeLinkOptions,
    });
  };

  const handleLikeClick = useCallback(() => {
    mobileLogger({
      logger: {
        eventName: `health_tip_detail_${magazine?.isLikedByMe ? 'disLike' : 'like'}_button_click`,
        pageName,
      },
    });

    if (!token) {
      showToast({ message: '로그인 후 이용할 수 있어요.' });
      setShowAuthModal(true);
      return;
    }

    if (magazine?.isLikedByMe) {
      cancelLikeContent();
      return;
    }
    likeContent();
  }, [cancelLikeContent, likeContent, magazine?.isLikedByMe, pageName]);

  const moveOtherMagazine = (magazine: RelatedMagazinesVo) => {
    moveToPage({
      webLink: magazine.isArticle
        ? `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`
        : `${ROUTES.FAQ.DETAIL.path}/${magazinePid}`,
      mobileScreenName: magazine.isArticle ? ROUTES.MAGAZINE.DETAIL.appScreenName : ROUTES.FAQ.DETAIL.appScreenName,
      params: {
        id: magazine.magazinePid,
        missionId: null,
      },
    });
  };

  const onClickShare = () => {
    logger({
      pageName,
      eventName: MAGAZINE_LOG_EVENT.UPPER_MAGAZINE_SHARE_CLICK,
    });
  };

  const onRecordLog = (logName: string) => {
    logger({
      pageName,
      eventName: logName,
    });
  };

  const onClickTag = (tagName: string) => {
    magazineLogger({
      pageName,
      eventName: MAGAZINE_LOG_EVENT.CLICK_MAGAZINE_TAG,
      payload: {
        magazinePid,
        tagType: MagazineTagType.FREE,
        tagName,
      },
    });

    navigate.navigateToMagazineTagList({ tagName });
  };

  const moveToSearch = () => {
    mobileLogger({
      logger: {
        eventName: MAGAZINE_LOG_EVENT.MOVE_TO_SEARCH_PAGE_CLICK,
        pageName,
      },
    });

    moveToPage({
      webLink: ROUTES.COMMON.SEARCH.path,
      mobileScreenName: ROUTES.COMMON.SEARCH.appScreenName,
      params: {
        type: 'magazine',
      },
    });
  };

  const moveToHospitalDetail = (hospitalPid: string) => {
    navigate.navigateToHospitalDetail({ hospitalPid });
  };

  const openExternalUrl = (url: string) => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER, {
        url,
      });
      return;
    }
    window.open(url);
  };
  const moveToAuthorHospital = (isDoctorProfile: boolean) => {
    if (magazine?.hospitalPid) {
      const hospitalPid = magazine.hospitalPid;

      magazineLogger({
        pageName,
        eventName: MAGAZINE_LOG_EVENT.CLICK_MOVE_HOSPITAL_FROM_MAGAZINE,
        payload: {
          magazinePid,
          hospitalPid,
          buttonType: isDoctorProfile ? 'doctor_profile' : 'card',
        },
      });

      if (magazine.author.externalLinkUrl) {
        openExternalUrl(magazine.author.externalLinkUrl);
        return;
      }
      moveToHospitalDetail(hospitalPid);
    }
  };

  const navigateToMagazineAuthorDetail = () => {
    if (!magazine) {
      return;
    }
    navigate.navigateToMagazineAuthorDetail({ authorPublicId: magazine.author.authorPublicId });
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });
  };

  const handleStickyButton = async () => {
    magazineLogger({
      eventName: MAGAZINE_LOG_EVENT.HEALTH_TIP_DETAIL_STICKY_BUTTON_CLICK,
      pageName,
      payload: {
        id: magazinePid,
        type: magazine?.bottomButtonType ?? '',
        platform: client.clientType,
      },
    });

    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });

    switch (magazine?.bottomButtonType) {
      case MagazineBottomButtonType.DIAGNOSIS:
        if (client.isWebView) {
          navigate.navigateCategorySelectWithJumpToHomeTab();
        } else {
          navigate.navigateToTelemedicineCategoriesScreen();
        }

        break;
      case MagazineBottomButtonType.QUESTION:
      default:
        if (!client.isWebView) {
          bottomButtonDynamicLink();
          return;
        }
        await clickProgressWrite();
        break;
    }
  };

  const magazineTocLogger = () => {
    magazineLogger({
      eventName: MAGAZINE_LOG_EVENT.CLICK_MAGAZINE_TOC,
      pageName,
      payload: {
        id: magazinePid,
      },
    });
  };

  const onClickPrevNextMagazine = (magazinePid: string) => {
    navigate.navigateToMagazineDetail({ magazinePid });
  };

  const onClickRelatedQna = (questionPid: string) => {
    magazineLogger({
      eventName: MAGAZINE_LOG_EVENT.MAGAZINE_RELATED_QNA_CLICK,
      pageName,
      payload: {
        magazinePid,
        questionPid,
      },
    });

    navigate.navigateToQnaDetailWithJumpToQnaTab({ questionPid });
  };

  const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (React.isValidElement(children) && children.props && children.props.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  const onClickOuterLink = (url: string) => {
    magazineLogger({
      eventName: MAGAZINE_LOG_EVENT.CLICK_OUTER_LINK,
      pageName,
      payload: {
        magazinePid,
        url,
      },
    });

    if (isWebView()) {
      navigate.navigateToPureWebViewAppScreen({
        uri: url,
      });
    } else {
      window.open(url, '_blank');
    }
  };

  const onClickOuterLinkWithLinkText = (url: string) => {
    if (isWebView()) {
      navigate.navigateToPureWebViewAppScreen({
        uri: url,
      });
    }
  };

  const onClickHospitalCta = (hospitalPid: string) => {
    magazineLogger({
      eventName: MAGAZINE_LOG_EVENT.CLICK_MOVE_HOSPITAL_FROM_CTA,
      pageName,
      payload: {
        magazinePid,
        hospitalPid,
      },
    });

    moveToHospitalDetail(hospitalPid);
  };

  const onClickMagazineBannerOutLink = (link: string) => {
    if (client.isWebView) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER, {
        url: link,
      });
      return;
    }

    airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
      campaign: 'web_content_magazine',
      content: 'web_content_bnr_details',
      term: magazine?.banner?.buttonPhrase || '',
      sub_id: magazine?.magazinePid,
      sub_id_1: magazine?.title,
      sub_id_2: diseaseTags?.[0]?.name || '',
      sub_id_3: departmentTags?.[0]?.name || '',
    });
  };

  const goBack = () => {
    if (missionId && !missionCompleted) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '아직 미션이 완료되지 않았어요',
          message: '티켓을 받으려면 미션을 완료해야해요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '다음에 하기',
              onClick: historyBack,
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '계속하기',
              onClick: closeModal,
            },
          ],
        },
      });
      return;
    }
    historyBack();
  };

  const isEnabledWebDiagnosisCategory =
    magazine?.bottomButtonType === MagazineBottomButtonType.DIAGNOSIS && !client.isWebView;

  useEffect(() => {
    if (!magazine) {
      return;
    }

    Pixel.trackCustom(EPixelEventName.PageIn, {
      ...(diseaseTags?.[0]?.name && { disease: diseaseTags?.[0]?.name }),
      ...(departmentTags?.[0]?.name && { subject: departmentTags?.[0]?.name }),
      ...(symptomTags?.[0]?.name && { symptom: symptomTags?.[0]?.name }),
    });

    setIsCujModalTrackingInfo({
      campaignName: 'web_content_magazine',
      trackContent: 'web_content_mdl_details',
      contentId: magazine.magazinePid,
      contentTitle: magazine.title,
      contentCategory: diseaseTags?.[0]?.name || '',
      contentSubCategory: departmentTags?.[0]?.name || '',
    });
  }, [magazine]);

  useEffect(() => {
    if (!missionId || !missionCompleted) {
      return;
    }

    completeMissionMutation.mutate(
      {
        request: { missionId: missionId },
      },
      {
        onSuccess: () => {
          broadCastChannel.postMessage(UserMissionTypeVo.MagazineRead);
        },
      }
    );
  }, [missionCompleted, missionId]);

  return {
    bannerAdExchange,
    totalRelatedMagazines: magazines,
    magazine,
    departmentTags: departmentTags ?? [],
    symptomTags: symptomTags ?? [],
    diseaseTags: diseaseTags ?? [],
    magazineContent: magazineContent ?? '',
    totalRelatedMagazinesTitle,
    magazineTagNames,
    prevMagazinePid,
    nextMagazinePid,
    pageName,
    magazineRelatedQna,
    copyUrl,
    bottomButtonText,
    timerVisible: Boolean(missionId),
    displayTime: displayTime,
    missionCompleted: missionCompleted,
    missionTargetTime: MISSION_TARGET_TIME,
    handleSendExposureLog,
    onClickTag,
    handleLikeClick,
    moveOtherMagazine,
    onClickShare,
    onRecordLog,
    moveToSearch,
    moveToHospital,
    moveToTotalSearch,
    handleStickyButton,
    moveToAuthorHospital,
    magazineTocLogger,
    onClickPrevNextMagazine,
    onClickRelatedQna,
    onClickOuterLink,
    extractTextFromChildren,
    onClickHospitalCta,
    goBack,
    onClickMagazineBannerOutLink,
    shouldShowEventText,
    navigateToMagazineAuthorDetail,
    onClickTopCta,
    onClickOuterLinkWithLinkText,
    getAirbridgeDynamicLink,
    airbridgeLinkOptions,
    isEnabledWebDiagnosisCategory,
  };
};

export default useMagazineDetailViewModel;
